import { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';

import {
	sendInteractGADataForSceneViewed,
	sendInteractGAInitData,
	sendMediaGACompleteData,
	sendMediaGAHeartBeatData,
	sendMediaGAInitData,
} from '../util/gAUtil';
import { isUsedOnMobile } from '../util/util';
import { usePlayer } from '../contexts/PlayerContext';

const HEARTBEAT_RATE = 30000;

interface useGoogleAnalyticProps {
	scenario: any;
	playerDesktopConfig: any;
	playerMobileConfig: any;
	configParams: any;
	currentMediaId: any;
	sceneConfig: any;
	accountId: any;
	isPlaying: boolean;
	currentPosition: number;
	mediaName: string;
}

const useGoogleAnalytic = ({
	scenario,
	playerDesktopConfig,
	playerMobileConfig,
	configParams,
	currentMediaId,
	sceneConfig,
	accountId,
	isPlaying,
	currentPosition,
	mediaName,
}: useGoogleAnalyticProps) => {
	// Context hooks
	const { duration: mediaDuration } = usePlayer();
	const [initializedGA, setInitializedGA] = useState(false);
	//media state
	const [isSentMediaInitData, setSendMediaInitData] = useState(false);
	const [isSentMediaHeartBeat, setSentMediaHeartBeat] = useState(false);
	const heartBeatIntervalRef = useRef<any>();
	const debounceTimerRef = useRef<any>();

	//interact state
	const [isSentInteractInitData, setSendInteractInitData] = useState(false);
	const [isSentInteractSceneViewData, setSendInteractSceneViewData] = useState(false);

	//prevent complete event being sent multiple times
	const debounced = (func: any, timeout = 1000) => {
		clearTimeout(debounceTimerRef.current);
		debounceTimerRef.current = setTimeout(func, timeout);
	};

	//init google analytic
	useEffect(() => {
		if (initializedGA || (!playerDesktopConfig && !playerMobileConfig) || configParams.fromSceneEditor === 'true') {
			return;
		}

		const gaMeasurementId = !isUsedOnMobile(configParams)
			? playerDesktopConfig?.analyticsSettings?.gaMeasurementId
			: playerMobileConfig?.analyticsSettings?.gaMeasurementId;
		if (!gaMeasurementId) {
			return;
		}

		ReactGA.initialize(gaMeasurementId, {
			gtagOptions: {
				send_page_view: false,
			},
		});
		setInitializedGA(true);
	}, [playerDesktopConfig, playerMobileConfig, initializedGA, scenario, configParams]);

	//media tracking here

	//initate media event
	useEffect(() => {
		if (initializedGA && !scenario && currentMediaId && mediaName && !isSentMediaInitData) {
			sendMediaGAInitData(currentMediaId, mediaName);
			setSendMediaInitData(true);
		}
	}, [initializedGA, scenario, currentMediaId, mediaName, isSentMediaInitData]);

	//init heartbeatinterval
	useEffect(() => {
		if (!isPlaying || scenario) {
			clearInterval(heartBeatIntervalRef.current);
			return;
		}

		const heartbeatInterval = setInterval(() => {
			if (!document.hidden) {
				setSentMediaHeartBeat(true);
			}
		}, HEARTBEAT_RATE);
		heartBeatIntervalRef.current = heartbeatInterval;

		return () => {
			clearInterval(heartBeatIntervalRef.current);
		};
	}, [isPlaying, scenario]);

	//send heartbeat data event
	useEffect(() => {
		if (isSentMediaHeartBeat) {
			sendMediaGAHeartBeatData(currentMediaId, currentPosition, mediaDuration);
			setSentMediaHeartBeat(false);
		}
	}, [isSentMediaHeartBeat, currentMediaId, currentPosition, mediaDuration]);

	//send complete data event
	useEffect(() => {
		if (scenario || !mediaDuration || !currentPosition) {
			return;
		}

		const timeGap = mediaDuration - currentPosition;
		if (timeGap >= 0 && timeGap <= 1) {
			debounced(() => {
				sendMediaGACompleteData(currentMediaId, mediaDuration);
			});
		}
	}, [scenario, currentPosition, currentMediaId, mediaDuration]);

	// end media tracking

	// interact tracking here
	//Initate interact event
	useEffect(() => {
		if (initializedGA && scenario && scenario.defaults.sceneId && !isSentInteractInitData) {
			const { id, metadata: { title = '' } = {}, scenes } = scenario;
			const homeSceneId = scenario.defaults.sceneId;
			const homeScene = (scenes ?? []) && scenes.find(({ id }: any) => id === homeSceneId);
			sendInteractGAInitData(homeScene, title, id);
			setSendInteractInitData(true);
		}
	}, [initializedGA, scenario, isSentInteractInitData]);

	//scene change so set isSentGASceneViewData = false to trigger send new event
	useEffect(() => {
		if (!initializedGA || !scenario || !currentMediaId) {
			return;
		}

		setSendInteractSceneViewData(false);
	}, [currentMediaId, accountId, scenario, initializedGA]);

	//Scene view event
	useEffect(() => {
		if (initializedGA && !isSentInteractSceneViewData && currentMediaId && scenario) {
			const { id, metadata: { title = '' } = {}, scenes } = scenario;
			const currentScene = (scenes ?? []) && scenes.find(({ id }: any) => id === sceneConfig.currentScene);
			sendInteractGADataForSceneViewed(currentScene, title, id);
			setSendInteractSceneViewData(true);
		}
	}, [initializedGA, currentMediaId, isSentInteractSceneViewData, scenario, sceneConfig.currentScene]);

	//end interact tracking
	return {};
};

export default useGoogleAnalytic;
